import { BrowserModule } from '@angular/platform-browser';
import { APP_INITIALIZER, LOCALE_ID, NgModule, CUSTOM_ELEMENTS_SCHEMA } from '@angular/core';
import { HttpClient, provideHttpClient, withInterceptorsFromDi, withXhr } from '@angular/common/http';
import { AppRoutingModule } from './app-routing.module';
import { AppComponent } from './app.component';
import { BrowserAnimationsModule } from '@angular/platform-browser/animations';
import { AuthModule } from './auth/auth.module';
import { SharedModule } from './shared/shared.module';
import { provideDateFnsAdapter } from '@angular/material-date-fns-adapter';
import {
  MAT_DATE_FORMATS,
  MAT_DATE_LOCALE,
} from '@angular/material/core';
import { from, Observable, switchMap } from 'rxjs';
import { AuthService } from './auth/services/auth.service';
import { MsalService } from './auth/sso/msal.service';
import { registerLocaleData } from '@angular/common';
import localePt from '@angular/common/locales/pt';
import { pt } from 'date-fns/locale';
import { QuillModule } from 'ngx-quill';
import { ImpersonationBannerComponent } from './shared/components/impersonation-banner/impersonation-banner.component';
import { CookieBannerComponent } from './shared/components/cookie-banner/cookie-banner.component';

registerLocaleData(localePt);

export const MY_FORMATS = {
  parse: {
    dateInput: 'MM/yyyy',
  },
  display: {
    dateInput: 'MM/yyyy',
    monthYearLabel: 'MMM yyyy',
    dateA11yLabel: 'PP',
    monthYearA11yLabel: 'MMMM yyyy',
  },
};

/**
 * Session restore at boot. MSAL goes first: it completes a redirect and picks up a cached SSO
 * account, which is what tells `checkLogin` this is an SSO session and not one to re-validate
 * against /account/checklogin. A failure there must not stop the app from booting on the legacy
 * path, so it is swallowed. `restoreSession` never fails either; it says what a failed
 * checklogin or settings call leaves behind.
 */
function initializeAppFactory(
  httpClient: HttpClient,
  authService: AuthService,
  msal: MsalService
): () => Observable<any> {
  return () =>
    from(msal.init().catch(() => undefined)).pipe(
      switchMap(() => authService.restoreSession())
    );
}

@NgModule({ declarations: [AppComponent],
    bootstrap: [AppComponent],
    schemas: [CUSTOM_ELEMENTS_SCHEMA], imports: [BrowserModule,
        BrowserAnimationsModule,
        AppRoutingModule,
        AuthModule,
        SharedModule,
        ImpersonationBannerComponent,
        CookieBannerComponent,
        QuillModule.forRoot()], providers: [
        {
            provide: APP_INITIALIZER,
            useFactory: initializeAppFactory,
            deps: [HttpClient, AuthService, MsalService],
            multi: true,
        },
        provideDateFnsAdapter(),
        { provide: MAT_DATE_FORMATS, useValue: MY_FORMATS },
        { provide: MAT_DATE_LOCALE, useValue: pt },
        { provide: LOCALE_ID, useValue: 'pt-PT' },
        provideHttpClient(withXhr(), withInterceptorsFromDi())
    ] })
export class AppModule {}
